import { InfoOutlineIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    Image,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    Tooltip,
    useToast
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { BsStars } from "react-icons/bs";
import { useDispatch } from "react-redux";
import { useGenerateVideoLinkMutation, useGenerateVideoPreviewsMutation, useGenerateVideoScriptMutation, useGetRemainingCredsQuery, useGetVideoStatusQuery, useRenderVideoMutation, useUpdateVideoLinkMutation } from "store/aiHub/aiGenerationApi";
import { useGetBmAdAccountsCountQuery, useGetBmAdAccountsQuery } from "store/BmAccounts/bmAccountApi";
import {
    useFinishUploadMutation,
    useStartUploadSessionMutation, useUploadChunkMutation,
    useUploadVideoMutation
} from 'store/driveUpload/uploadApi';
import { cancelUpload, setFiles, setIsWidgetOpen, setUploadAbortControllers, updateUploadStatus } from "store/driveUpload/uploadSlice";
import { isValidCustomScript, removeEmptyValues } from "utils/functions";
import * as Yup from 'yup';
import AvatarTabPanel from "./AvatarTabPanel";
import ManualUploadForm from "./ManualUploadForm";
import MusicTabPanel from "./MusicTabPanel";
import ProductUploadForm from "./ProductUploadForm";
import RenderedVideos from "./RenderedVideos";
import ScriptSelector from "./ScriptSelector";
import VideoPreviews from "./VideoPreviews";
import VideoSettingsForm from "./VideoSettingsForm";
import VoicesTabPanel from "./VoiceTabPanel";

const fromApiToFormValues = (linkData) => ({
    brandName: linkData?.link?.title?.split(' ')[0] || '',
    description: linkData?.link?.description || '',
    logo_url: linkData?.link?.logo_url || '',
    id: linkData?.id || '',
    assets: [
        ...(linkData?.link?.image_urls || []).map(url => ({
            url,
            type: 'image',
            selected: true,
        })),
        ...(linkData?.link?.video_urls || []).map(url => ({
            url,
            type: 'video',
            selected: true,
        }))
    ],
});

const AiVideoModal = ({ selectedAdAccounts, isOpen, onClose }) => {

    const toast = useToast();
    const productUploadRef = useRef();
    const manualUploadRef = useRef();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [startUploadSession] = useStartUploadSessionMutation();
    const [uploadChunk] = useUploadChunkMutation();
    const [finishUpload] = useFinishUploadMutation();
    const [afterValue, setAftervalue] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [videoLinkData, setVideoLinkData] = useState([null]);
    const [uploadType, setUploadType] = useState('');
    const [videoUpdatedData, setVideoUpdatedData] = useState([]);
    const [generatedScriptData, setGeneratedScriptData] = useState([null]);
    const [formValues, setFormValues] = useState({ brandName: '', assets: [] });
    const [manualFormValues, setManualFormValues] = useState({ brandName: '', assets: [] });
    const [videoSettings, setVideoSettings] = useState({
        aspectRatio: "9:16",
        duration: "15",
        language: "en",
        targetAudience: "",
    });
    const [selectedScriptData, setSelectedScriptData] = useState({
        script: '',
        script_style: '',
    });
    const [selectedTab, setSelectedTab] = useState('avatar');
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [selectedVoiceAccent, setSelectedVoiceAccent] = useState(null);
    const [selectedMusic, setSelectedMusic] = useState(null);
    const [previewId, sePreviewId] = useState(null);
    const [isStatusGenerating, setIsStatusGenerating] = useState(false);
    const [selectedPreviews, setSelectedPreviews] = useState([]);
    const [selectedRenderVideos, setSelectedRenderVideos] = useState([]);
    const [isRendering, setIsRendering] = useState(false);
    const [renderedResponses, setRenderedResponses] = useState([]);
    const [localFiles, setLocalFiles] = useState([]);

    const tabIndexMap = {
        avatar: 0,
        voices: 1,
        music: 2
    };

    const indexTabMap = {
        0: 'avatar',
        1: 'voices',
        2: 'music'
    };

    const { data: accountsCount } = useGetBmAdAccountsCountQuery();
    const totalCount = accountsCount?.summary?.total_count ?? 20;
    const isSearching = !!searchTerm?.trim();
    const effectivePageSize = isSearching ? String(totalCount) : "20";

    const { data: AdAccountData, refetch: refetchAdaccountData, isLoading: isAdAccountLoading, isFetching: isAdAccountFetching } = useGetBmAdAccountsQuery(
        {
            search: searchTerm ? searchTerm : '',
            pageSize: effectivePageSize,
            after: searchTerm ? '' : afterValue,
        },
        { skip: !isOpen }
    );

    const [generateVideoLink, { isLoading: isLinkGenerating }] = useGenerateVideoLinkMutation();
    const [updateVideoLink, { isLoading: isLinkUpdating }] = useUpdateVideoLinkMutation();
    const [generateVideoScript, { isLoading: isScriptGenerating }] = useGenerateVideoScriptMutation();
    const [generateVideoPreviews, { isLoading: isVideoPreviewsGenerating }] = useGenerateVideoPreviewsMutation();
    const { data: videoStatus, isLoading: isVideoStatusLoading, refetch: refetchVideoStatus } = useGetVideoStatusQuery(
        { previewId },
        {
            skip: !previewId,
        }
    );
    const [renderVideo, { isLoading: isVideorendering }] = useRenderVideoMutation();
    const [uploadVideo, { isLoading: isVideoLoading }] = useUploadVideoMutation();
    const {data: remainingCreds, isLoading: isRemainingCredsLoading, isError} = useGetRemainingCredsQuery();

    const [retryCount, setRetryCount] = useState(0);
    const maxRetries = 4;
    const retryDelay = 12000;

    useEffect(() => {
        // Only proceed if videoStatus is available and not loading
        if (videoStatus && !isVideoStatusLoading) {
            // Check if previews is an empty array
            if (videoStatus?.previews && videoStatus?.previews?.length === 0) {
                if (retryCount < maxRetries) {
                    const timer = setTimeout(() => {
                        console.log(`Retrying fetch for previewId: ${previewId}, attempt: ${retryCount + 1}`);
                        refetchVideoStatus();
                        setRetryCount(prevCount => prevCount + 1);
                    }, retryDelay);
                    return () => clearTimeout(timer);
                } else {
                    console.log(`Max retries reached for previewId: ${previewId}. Previews still empty.`);
                    setIsStatusGenerating(false);
                    // Optionally, handle the case where max retries are reached and previews are still empty
                }
            } else if (videoStatus.previews && videoStatus.previews.length > 0) {
                // If previews are found, reset retry count for future use
                setRetryCount(0);
                setIsStatusGenerating(false);
            }
        }
    }, [videoStatus, isVideoStatusLoading, retryCount, previewId]);

    useEffect(() => {
        if (hasSearched && searchTerm === '') {
            refetchAdaccountData();
        }
    }, [searchTerm]);

    const productLinkFormik = useFormik({
        initialValues: {
            productLink: '',
        },
        validationSchema: Yup.object({
            productLink: Yup.string()
                .url('Enter a valid URL')
                .required('Product link is required'),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            handleNext();
            setUploadType('')

            generateVideoLink({ url: values.productLink })
                .unwrap()
                .then((response) => {
                    const responseData = fromApiToFormValues(response);
                    setVideoLinkData(responseData);
                })
                .catch((error) => {
                    handleBack();
                    toast({
                        title: 'Link analysis failed',
                        description: error?.data?.message || 'Something went wrong',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                        position: 'top-right',
                    });
                })
                .finally(() => {
                    setSubmitting(false);
                });

        },
    });

    const handleFormSubmit = (formValues) => {
        handleNext();

        // Filter selected assets
        const selectedAssets = formValues?.assets?.filter(asset => asset.selected);

        // Separate image and video URLs
        const image_urls = selectedAssets
            .filter(asset => asset.type === 'image')
            .map(asset => asset.url);

        const video_urls = selectedAssets
            .filter(asset => asset.type === 'video')
            .map(asset => asset.url);

        // Build payload matching your required format
        const initialPayload = {
            title: formValues?.brandName,
            ...(image_urls.length > 0 && { image_urls }),
            ...(video_urls.length > 0 && { video_urls }),
            logo_url: formValues?.logo_url || '',
            description: formValues?.description || '',
        };

        const payload = removeEmptyValues(initialPayload);

        updateVideoLink({ payload, linkId: videoLinkData?.id })
            .unwrap()
            .then((response) => {
                setVideoUpdatedData(response);
            })
            .catch((error) => {
                handleBack();
                toast({
                    title: 'Failed to update',
                    status: 'error',
                    description: error?.data?.message || 'Something went wrong',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
            });
    };

    const handleManualSubmit = (manualFormValues) => {
        handleNext();

        // Filter selected assets
        const selectedAssets = manualFormValues?.assets?.filter(asset => asset.selected);

        // Separate image and video URLs
        const image_urls = selectedAssets
            .filter(asset => asset.type === 'image')
            .map(asset => asset.url);

        const video_urls = selectedAssets
            .filter(asset => asset.type === 'video')
            .map(asset => asset.url);

        // Build payload matching your required format
        const initialPayload = {
            title: manualFormValues?.brandName,
            ...(image_urls.length > 0 && { image_urls }),
            ...(video_urls.length > 0 && { video_urls }),
            logo_url: manualFormValues?.logo_url || '',
            description: manualFormValues?.description || '',
        };

        const payload = removeEmptyValues(initialPayload);

        updateVideoLink({ payload, linkId: manualFormValues?.videoId })
            .unwrap()
            .then((response) => {
                setVideoUpdatedData(response);
            })
            .catch((error) => {
                handleBack();
                toast({
                    title: 'Failed to update',
                    status: 'error',
                    description: error?.data?.message || error?.message || 'Something went wrong',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
            });
    };

    const handleGenerateScript = () => {
        handleNext();
        setIsStatusGenerating(true);

        const initialPayload = {
            url: videoUpdatedData?.url,
            title: videoUpdatedData?.title,
            description: videoUpdatedData?.description,
            language: videoSettings?.language,
            video_length: videoSettings?.duration,
            target_audience: videoSettings?.targetAudience,
            script_styles: [
                "BenefitsV2",
                "CallToActionV2",
                "GenzWriter",
                "ProductHighlightsV2",
                "StoryTimeWriter"
            ]
        };

        const payload = removeEmptyValues(initialPayload);

        generateVideoScript(payload)
            .unwrap()
            .then((response) => {
                setGeneratedScriptData(response);
            })
            .catch((error) => {
                handleBack();
                setIsStatusGenerating(false);
                toast({
                    title: 'Failed to generate',
                    status: 'error',
                    description: error?.data?.message || error?.message || 'Something went wrong',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
            });
    };

    const handleGenerateVideoPreviews = () => {
        handleNext();

        const videoLink = uploadType === 'manual' ? manualFormValues?.videoId : videoLinkData?.id;

        const initialPayload = {
            name: videoUpdatedData?.title,
            target_platform: "facebook",
            target_audience: videoSettings?.targetAudience,
            language: videoSettings?.language,
            video_length: videoSettings?.duration,
            aspect_ratio: videoSettings?.aspectRatio?.replace(':', 'x'),
            override_avatar: selectedAvatar?.id,
            override_voice: selectedVoiceAccent?.id,
            override_script: selectedScriptData?.script,
            background_music_url: selectedMusic?.url,
            background_music_volume: 0.5,
            voiceover_volume: 0.5,
            link: videoLink,
            no_background_music: false,
            no_caption: false,
            no_emotion: false,
            caption_style: "normal-black",
            caption_offset_x: 0.5,
            caption_offset_y: 0.5,
            caption_setting: {
                style: "normal-black",
                offset: { x: 0, y: 0.4 },
                font_family: "Montserrat",
                font_size: 40,
                font_style: "italic",
                background_color: "#FFFFFFFF",
                text_color: "#000000FF",
                highlight_text_color: "#007BFFFF",
                text_shadow: "0px 1px 2px rgba(0, 0, 0, 0.25)",
                hidden: false
            },
            visual_styles: [
                "DynamicProductTemplate",
                "GreenScreenEffectTemplate",
                "QuickTransitionTemplate",
                "SimpleAvatarOverlayTemplate",
                "VanillaTemplate",
                "LegoScriptNegativeHook",
                "LegoScriptAmazedHook",
                "LegoScriptCannotBelieveHook",
                "LegoScriptPremiumFeelingHook",
                "LegoScriptMissingOutAlertHook"
            ],
            // aspect_ratios: [
            //     "9x16",
            //     "16x9",
            //     "1x1"
            // ]
        };

        const payload = removeEmptyValues(initialPayload);

        generateVideoPreviews(payload)
            .unwrap()
            .then((response) => {
                sePreviewId(response?.id);
            })
            .catch((error) => {
                handleBack();
                toast({
                    title: 'Failed to generate',
                    status: 'error',
                    description: error?.data?.message || 'Something went wrong',
                    duration: 3000,
                    isClosable: true,
                    position: 'top-right',
                });
            });
    };



    const handleVideoRender = async () => {
        try {
            handleNext();
            setIsRendering(true);

            const renderAndPoll = async (previewId) => {
                const payload = { media_job: previewId };

                // Initial render request
                let result = await renderVideo({ payload, id: videoStatus?.id }).unwrap();

                // If video_output not available, wait 1 minute then retry up to 2 more times
                if (!result.video_output) {
                    await new Promise(resolve => setTimeout(resolve, 70000)); // wait 1 minute and 10 secs

                    let attempts = 0;
                    while (!result.video_output && attempts < 4) {
                        await new Promise(resolve => setTimeout(resolve, 12000)); // wait 12 seconds
                        attempts++;

                        const updated = await renderVideo({ payload, id: videoStatus?.id }).unwrap();
                        result = updated;

                        if (result.video_output) {
                            break;
                        }
                    }
                }

                return result;
            };

            const renderPromises = selectedPreviews?.map(previewId => renderAndPoll(previewId));
            const responses = await Promise.all(renderPromises);
            setRenderedResponses(responses);

        } catch (error) {
            console.error(error);
            setIsRendering(false);
            toast({
                title: 'Failed to render',
                status: 'error',
                description: error?.data?.message || 'Something went wrong',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });
        } finally {
            setIsRendering(false);
        }
    };


    const handleUploadAll = async (filesToUpload) => {
        // console.log((window.navigator.onLine ? 'on' : 'off') + 'line');

        window.addEventListener('online', () => {
            toast({
                title: "You are back online!",
                status: "success",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        window.addEventListener('offline', () => {
            toast({
                title: "You are offline. Some functionality may be unavailable.",
                status: "error",
                duration: 4000,
                isClosable: true,
                position: "top-right",
            });
        });

        const uploadPromises = [];

        for (const file of filesToUpload) {
            const adsAccountsArray = selectedAdAccounts.map(account => ({
                id: account.id,
                adsAccountId: account.adsAccountId,
                adsAccountName: account.adsAccountName,
                businessName: account.businessName,
                businessId: account.businessId
            }));

            if (file.type.startsWith("video/")) {
                const uploadPromises = adsAccountsArray.map(selectedAdAccount => {
                    return (async () => {
                        const abortController = new AbortController();
                        const controllerKey = `${file.name}-${selectedAdAccount.adsAccountId}`;
                        dispatch(setUploadAbortControllers({ [controllerKey]: abortController }));

                        try {
                            dispatch(updateUploadStatus({
                                fileName: file.name,
                                accountId: selectedAdAccount.adsAccountId,
                                accountName: selectedAdAccount.adsAccountName,
                                status: "uploading",
                                progress: 10
                            }));

                            const payload = {
                                aiGenerated: true,
                                url: file?.thumbnail,
                                adsAccount: {
                                    adsAccountId: selectedAdAccount.adsAccountId,
                                    adsAccountName: selectedAdAccount.adsAccountName,
                                    businessName: selectedAdAccount.businessName,
                                    businessId: selectedAdAccount.businessId
                                }
                            };

                            await uploadVideo({ payload });

                            dispatch(updateUploadStatus({
                                fileName: file.name,
                                accountId: selectedAdAccount.adsAccountId,
                                accountName: selectedAdAccount.adsAccountName,
                                status: "done",
                                progress: 100
                            }));
                        } catch (error) {
                            const reason = error?.message || 'Upload failed';
                            const isCancelled = abortController?.signal?.aborted;

                            dispatch(updateUploadStatus({
                                fileName: file.name,
                                accountId: selectedAdAccount.adsAccountId,
                                accountName: selectedAdAccount.adsAccountName,
                                status: isCancelled ? "cancelled" : "failed",
                                progress: 0,
                                reason: isCancelled ? "Upload cancelled" : reason
                            }));

                            if (!isCancelled) {
                                toast({
                                    title: "Upload Error",
                                    description: reason,
                                    status: "error",
                                    duration: 5000,
                                    isClosable: true,
                                    position: "top-right",
                                });
                            }
                        }
                    })();
                });

                // Wait for all uploads to finish 
                Promise.allSettled(uploadPromises);
            }
        }

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);
    };

    const handleNext = () => {
        setCurrentStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const handleModalClose = () => {
        dispatch(setFiles([]));
        onClose();
        localStorage.removeItem('selectedAdspressoAdsAccount');
        setAftervalue('');
        setCurrentStep(0);
    };

    const handlePreviewCheckboxChange = (media_job) => {
        const updatedSelection = selectedPreviews?.includes(media_job)
            ? selectedPreviews?.filter((item) => item !== media_job)
            : [...selectedPreviews, media_job];

        setSelectedPreviews(updatedSelection);
    };

    const handleRenderVideoChange = (video) => {
        const updatedSelection = selectedRenderVideos?.includes(video)
            ? selectedRenderVideos?.filter((item) => item?.id !== video?.id)
            : [...selectedRenderVideos, video];

        setSelectedRenderVideos(updatedSelection);

        const timestamp = Date.now();

        // Transform format
        const transformedFiles = updatedSelection.map((item, index) => {

            // Dummy size: 16–20 MB
            const sizeInMB = Math.floor(Math.random() * (20 - 16 + 1)) + 16;
            const sizeInBytes = sizeInMB * 1024 * 1024;

            // Unique name: name + timestamp + index
            const baseName = item.name?.replace(/\s+/g, "_") || "video";
            const fileName = `${baseName}_${timestamp}_${index + 1}.mp4`;

            const dummyData = new Uint8Array(1024 * 1024 * 16); // 16MB of zeroes
            const fakeFile = new File([dummyData], fileName, {
                type: "video/mp4"
            });

            return {
                file: fakeFile,
                name: fileName,
                size: sizeInBytes,
                type: "video/mp4",
                thumbnail: item.video_output || URL.createObjectURL(fakeFile), // fallback
                progress: 0,
                status: "pending",
            };
        });

        dispatch(setFiles(transformedFiles));
        setLocalFiles(transformedFiles);
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={() => handleModalClose()} size={{ base: "6xl" }} scrollBehavior="inside" closeOnOverlayClick={false} closeOnEsc={false}>
                <ModalOverlay />
                <ModalContent minH="80vh">
                    <ModalHeader>
                        <ModalCloseButton />
                        <Flex gap={2} direction={'column'}>
                            <Flex alignItems={'center'} gap={2}>
                                <BsStars />
                                <Text>AI Video Generation</Text>
                                <Tooltip
                                    borderRadius={5}
                                    label={
                                        <Flex direction={'column'} gap={2} fontSize="12px" p="5px">
                                            <Text fontWeight={'bold'}>AI Video Generation</Text>
                                            <Text>AI-generated video ads are created using your product information and visuals. By assembling product highlights, voiceovers, and dynamic visuals, the AI helps generate high-performing video ads optimised for various platforms.</Text>
                                            <Flex direction={'column'} gap={2}>
                                                <Text fontWeight={'bold'}>Types of AI Variations:</Text>
                                                <Text>Product URL Based: Extracts product details from your URL to auto-generate video content.</Text>
                                                <Text>Asset Based: Uses your uploaded visuals to create fully edited video ads.</Text>
                                            </Flex>
                                            <Text>Note: Hustle does not guarantee the completeness, accuracy, or reliability of AI-generated videos.</Text>
                                        </Flex>
                                    }
                                    fontSize="md"
                                >
                                    <InfoOutlineIcon pointerEvents="all" />
                                </Tooltip>
                            </Flex>
                        </Flex>
                    </ModalHeader>
                    <Divider mb={'10px'} />
                    <ModalBody>
                    {isRemainingCredsLoading ? (
                        <Flex align="center" justify="center" minH="65vh">
                            <Spinner size="lg" />
                        </Flex>
                        ) : isError ? (
                        <Flex alignItems="center" justifyContent="center" minH="65vh">
                            <Text color={'red.500'}>We’re making some updates. Service will resume shortly. Thanks for your patience!</Text>
                        </Flex>
                        ) : 
                        <Flex direction={'row'} minH={'65vh'}>
                            {currentStep === 0 && (
                                <>
                                    <Flex width="full" alignItems={'center'} justifyContent={'center'}>
                                        <form onSubmit={productLinkFormik.handleSubmit}>
                                            <Flex direction={'column'} gap={6} w="100%" maxW="4xl">
                                                <Heading fontSize={'3xl'}>Share your product link to generate a video</Heading>
                                                <Flex direction={'column'} gap={1}>
                                                    <Input
                                                        placeholder="e.g. amazon product link, shopify product link, app store link, etc."
                                                        size="md"
                                                        borderRadius="md"
                                                        name="productLink"
                                                        value={productLinkFormik.values.productLink}
                                                        onChange={productLinkFormik.handleChange}
                                                        onBlur={productLinkFormik.handleBlur}
                                                    />
                                                    {productLinkFormik.touched.productLink && productLinkFormik.errors.productLink ? (
                                                        <Text color="red.500" fontSize={'14px'}>{productLinkFormik.errors.productLink}</Text>
                                                    ) : null}
                                                </Flex>

                                                <Flex alignItems={'center'} justifyContent={'center'} gap={4}>
                                                    <Button type="button" size="md" onClick={() => {
                                                        handleNext();
                                                        setUploadType('manual')
                                                    }}>
                                                        Upload manually
                                                    </Button>
                                                    <Button
                                                        colorScheme='brand'
                                                        type="submit"
                                                        size="md"
                                                        isLoading={productLinkFormik.isSubmitting}
                                                    >
                                                        Analyze URL
                                                    </Button>
                                                </Flex>
                                            </Flex>
                                        </form>
                                    </Flex>
                                </>
                            )}

                            {currentStep === 1 && (
                                <Flex width="full" alignItems={'center'} justifyContent={'center'}>
                                    {uploadType === 'manual' ?
                                        <ManualUploadForm
                                            ref={manualUploadRef}
                                            isLoading={isLinkGenerating}
                                            onSubmit={handleManualSubmit}
                                            onFormUpdate={(values) => setManualFormValues(values)}
                                        />
                                        :
                                        <ProductUploadForm
                                            ref={productUploadRef}
                                            isLoading={isLinkGenerating}
                                            initialValues={videoLinkData}
                                            onSubmit={handleFormSubmit}
                                            onFormUpdate={(values) => setFormValues(values)}
                                        />
                                    }
                                </Flex>
                            )}

                            {currentStep === 2 && (
                                <>
                                    {isLinkUpdating ?
                                        <Flex direction={"column"} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
                                            <Image src="/Ai_ImageLoader.gif" alt="generating..." boxSize="200px" objectFit="contain" width="100%" />
                                            <Flex direction={"column"} alignItems={"center"} gap={2}>
                                                <Text fontWeight={"bold"} fontSize={"14px"}>
                                                    Loading video settings...
                                                </Text>
                                            </Flex>
                                        </Flex>
                                        :
                                        <Flex width="full" alignItems={'center'} justifyContent={'center'}>
                                            <VideoSettingsForm
                                                values={videoSettings}
                                                onChange={setVideoSettings}
                                                aiTargetAudiences={videoUpdatedData?.ai_target_audiences}
                                            />
                                        </Flex>
                                    }
                                </>
                            )}

                            {currentStep === 3 && (
                                <>
                                    {isScriptGenerating ?
                                        <Flex direction={'column'} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
                                            <Image
                                                src="/Ai_ImageLoader.gif"
                                                alt="generating..."
                                                boxSize="200px"
                                                objectFit="contain"
                                                width="100%"
                                            />
                                            <Flex direction={'column'} alignItems={'center'} gap={2}>
                                                <Text fontWeight={'bold'} fontSize={'14px'}>AI generating scripts...</Text>
                                                <Text fontSize={'14px'}>This step may take up to 45 seconds</Text>
                                            </Flex>
                                        </Flex>
                                        :
                                        <Flex width="full" justifyContent={'center'}>
                                            <ScriptSelector
                                                generatedScripts={generatedScriptData?.generated_scripts || []}
                                                onSelect={(data) => setSelectedScriptData(data)}
                                            />
                                        </Flex>
                                    }
                                </>
                            )}

                            {currentStep === 4 && (
                                <>
                                    {isScriptGenerating ?
                                        <Flex width="full" alignItems="center" justifyContent="center" h="60vh">
                                            <Image
                                                src="/Ai_ImageLoader.gif"
                                                alt="generating..."
                                                boxSize="200px"
                                                objectFit="contain"
                                                width="100%"
                                            />
                                        </Flex>
                                        :
                                        <Flex width="full" justifyContent={'center'} >
                                            <Box width="840px" maxH="500px" display="flex" flexDirection="column">
                                                <Heading fontSize="2xl" mb={2}>
                                                    Choose Avatar, Voice, Music
                                                </Heading>
                                                <Tabs
                                                    colorScheme="brand"
                                                    variant={'enclosed-colored'}
                                                    index={tabIndexMap[selectedTab]}
                                                    onChange={(index) => {
                                                        const newTab = indexTabMap[index];
                                                        if (newTab !== selectedTab) {
                                                            setSelectedTab(newTab);
                                                        }
                                                    }}
                                                    isLazy>
                                                    <TabList >
                                                        <Tab fontSize={'14px'}
                                                        >Avatar</Tab>
                                                        <Tab fontSize={'14px'}
                                                        >Voices</Tab>
                                                        <Tab fontSize={'14px'}
                                                        >Music</Tab>
                                                    </TabList>
                                                    <TabPanels>
                                                        {/* === Avatar Tab Content === */}
                                                        <TabPanel>
                                                            <Flex justifyContent={'center'}>
                                                                <AvatarTabPanel
                                                                    selectedAvatar={selectedAvatar}
                                                                    onSelectAvatar={setSelectedAvatar}
                                                                />
                                                            </Flex>
                                                        </TabPanel>
                                                        {/* === Voices Tab Content (Placeholder) === */}
                                                        <TabPanel>
                                                            <Flex justifyContent={'center'}>
                                                                <VoicesTabPanel
                                                                    selectedTab={selectedTab}
                                                                    selectedVoice={selectedVoice}
                                                                    onSelectVoice={setSelectedVoice}
                                                                    selectedVoiceAccent={selectedVoiceAccent}
                                                                    onSelectAccent={setSelectedVoiceAccent}
                                                                />
                                                            </Flex>
                                                        </TabPanel>

                                                        {/* === Music Tab Content (Placeholder) === */}
                                                        <TabPanel>
                                                            <Flex justifyContent={'center'}>
                                                                <MusicTabPanel
                                                                    selectedTab={selectedTab}
                                                                    selectedMusic={selectedMusic}
                                                                    onSelectMusic={setSelectedMusic}
                                                                />
                                                            </Flex>
                                                        </TabPanel>
                                                    </TabPanels>
                                                </Tabs>
                                            </Box>
                                        </Flex>
                                    }
                                </>
                            )}

                            {currentStep === 5 && (
                                <>
                                    {isStatusGenerating ?
                                        <Flex direction={'column'} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
                                            <Image
                                                src="/Ai_ImageLoader.gif"
                                                alt="generating..."
                                                boxSize="200px"
                                                objectFit="contain"
                                                width="100%"
                                            />
                                            <Flex direction={'column'} alignItems={'center'} gap={2}>
                                                <Text fontWeight={'bold'} fontSize={'14px'}>AI generating creative avatars...</Text>
                                                <Text fontSize={'14px'}>This step may take up to a minute</Text>
                                            </Flex>
                                        </Flex>
                                        :
                                        <Flex width="full" justifyContent={'center'}>
                                            <VideoPreviews
                                                previewData={videoStatus}
                                                selectedPreviews={selectedPreviews}
                                                onSelectedPreviews={handlePreviewCheckboxChange}
                                            />
                                        </Flex>
                                    }
                                </>
                            )}

                            {currentStep === 6 && (
                                <>
                                    {isRendering ?
                                        <Flex direction={'column'} width="full" alignItems="center" justifyContent="center" h="60vh" gap={2}>
                                            <Image
                                                src="/Ai_ImageLoader.gif"
                                                alt="generating..."
                                                boxSize="200px"
                                                objectFit="contain"
                                                width="100%"
                                            />
                                            <Flex direction={'column'} alignItems={'center'} gap={2}>
                                                <Text fontWeight={'bold'} fontSize={'14px'}>AI rendering your video...</Text>
                                                <Text fontSize={'14px'}>This step may take up to a minute</Text>
                                            </Flex>
                                        </Flex>
                                        :
                                        <Flex width="full" justifyContent={'center'}>
                                            <RenderedVideos
                                                renderedData={renderedResponses}
                                                selectedRenderVideos={selectedRenderVideos}
                                                onSelectedRenderedVideos={handleRenderVideoChange}
                                            />
                                        </Flex>
                                    }
                                </>
                            )}
                        </Flex>
                    }
                    </ModalBody>
                    <ModalFooter>
                        <Flex width="100%" justifyContent="space-between" alignItems="center">
                            {/* Left Side */}
                            {currentStep === 0 ? (
                                <Flex alignItems="center" gap="8px">
                                    <InfoOutlineIcon fontSize="12px" />
                                    <Text fontSize="12px">
                                        AI-generated videos will be stored only in the "My Drive" of the designated Ad Accounts.
                                    </Text>
                                </Flex>
                            ) :
                                <Flex>
                                </Flex>
                            }

                            {currentStep === 6 ? (
                                <Flex alignItems="center" gap="8px">
                                    <InfoOutlineIcon fontSize="12px" />
                                    <Text fontSize="12px">
                                        After you click Save, your video will start uploading. Once the upload is complete, it may take 5–10 minutes for the video to appear in the My Drive of the selected Ad Account(s).
                                    </Text>
                                </Flex>
                            ) :
                                <Flex>
                                </Flex>
                            }

                            {/* Right Side: Back or Next Button */}
                            <Flex gap={2}>

                                {currentStep === 1 && (
                                    <>
                                        {uploadType === 'manual' ?
                                            <Flex gap={2}>
                                                <Button 
                                                    colorScheme="gray"
                                                    variant="solid"
                                                    size="sm" 
                                                    onClick={() => {
                                                        setUploadType('');
                                                        handleBack();
                                                }}>
                                                    Back
                                                </Button>
                                                <Button
                                                    colorScheme='brand'
                                                    size="sm"
                                                    onClick={() => manualUploadRef?.current?.submitForm()}
                                                    isDisabled={
                                                        !manualFormValues.brandName ||
                                                        !manualFormValues.videoId ||
                                                        manualFormValues.assets?.length < 3 ||
                                                        manualFormValues.assets.filter(asset => asset.selected).length < 3
                                                    }
                                                >
                                                    Next
                                                </Button>
                                            </Flex>
                                            :
                                            <Flex gap={2}>
                                                 <Button 
                                                    colorScheme="gray"
                                                    variant="solid"
                                                    size="sm" 
                                                    onClick={handleBack}
                                                >
                                                    Back
                                                </Button>
                                                <Button
                                                    colorScheme='brand'
                                                    size="sm"
                                                    onClick={() => productUploadRef?.current?.submitForm()}
                                                    isDisabled={
                                                        !formValues.brandName ||
                                                        formValues.assets?.length < 3 ||
                                                        formValues.assets.filter(asset => asset.selected).length < 3
                                                    }
                                                >
                                                    Next
                                                </Button>
                                            </Flex>
                                        }
                                    </>
                                )}

                                {currentStep === 2 && (
                                    <Flex gap={2}>
                                        <Button 
                                            colorScheme="gray"
                                            variant="solid"
                                            size="sm" 
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            colorScheme='brand'
                                            size="sm"
                                            onClick={handleGenerateScript}
                                        >
                                            Next
                                        </Button>
                                    </Flex>
                                )}

                                {currentStep === 3 && (
                                    <Flex gap={2}>
                                        <Button 
                                            colorScheme="gray"
                                            variant="solid"
                                            size="sm" 
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            colorScheme='brand'
                                            size="sm"
                                            isDisabled={selectedScriptData.script_style === 'custom'
                                                ? !isValidCustomScript(selectedScriptData?.script)
                                                : selectedScriptData.script.trim() === '' || isScriptGenerating}
                                            onClick={handleNext}
                                        >
                                            Next
                                        </Button>
                                    </Flex>
                                )}

                                {currentStep === 4 && (
                                    <Flex gap={2}>
                                        <Button 
                                            colorScheme="gray"
                                            variant="solid"
                                            size="sm" 
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            colorScheme='brand'
                                            size="sm"
                                            onClick={handleGenerateVideoPreviews}
                                        >
                                            Next
                                        </Button>
                                    </Flex>
                                )}

                                {currentStep === 5 && (
                                    <Flex gap={2}>
                                        <Button 
                                            colorScheme="gray"
                                            variant="solid"
                                            size="sm" 
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            colorScheme='brand'
                                            size="sm"
                                            isDisabled={selectedPreviews?.length === 0}
                                            onClick={handleVideoRender}
                                        >
                                            {`Render(${selectedPreviews?.length} video)`}
                                        </Button>
                                    </Flex>
                                )}

                                {currentStep === 6 && (
                                    <Flex gap={2}>
                                        <Button 
                                            colorScheme="gray"
                                            variant="solid"
                                            size="sm" 
                                            onClick={handleBack}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            colorScheme='brand'
                                            size="sm"
                                            isDisabled={localFiles?.length === 0}
                                            onClick={() => {
                                                handleUploadAll(localFiles);
                                                dispatch(setIsWidgetOpen(true));
                                                onClose();
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </Flex>
                                )}
                            </Flex>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal >
        </>
    );
};

export default AiVideoModal;